import React, { useContext, useEffect, useState } from 'react';
import './styles.scss';
import Dropdown from '../../components/Dropdown';
import { useTranslation } from 'react-i18next';
import AppStateContext from '../../AppStateProvider/context';
import { ScreenEnum } from '../../enums';
import ResetOverlay from '../../components/ResetOverlay';
import { WelcomeProps } from './interfaces';
import RadioButtons from '../../components/RadioButtons';
import { deviceTypeList } from '../../utils/deviceTypeList';

export default function Welcome(props: WelcomeProps): JSX.Element {
	const { state, dispatch } = useContext(AppStateContext);
	const { t, i18n } = useTranslation();
	const [resetOverlay, setResetOverlay] = useState<boolean>(false);
	const [nextScreen, setNextScreen] = useState<ScreenEnum>(
		ScreenEnum.validation
	);
	const params = new URLSearchParams(window.location.search);
	const chromebox =
		params.get('chromebox') === 'true' || state.hardware === 'Chromebox';
	const options = [
		{
			selected: nextScreen === ScreenEnum.setSecond,
			text: t('WelcomeChangeOrientation'),
			value: ScreenEnum.setSecond,
			displayNone: chromebox
		},
		{
			selected: nextScreen === ScreenEnum.password,
			text: t('WelcomeSetNewPassword'),
			value: ScreenEnum.password,
			displayNone: state.apk,
		},
		{
			selected: nextScreen === ScreenEnum.welcome,
			text: t('WelcomeResetConfig'),
			value: ScreenEnum.welcome,
		},
		{
			selected: nextScreen === ScreenEnum.exit,
			text: t('WelcomeTurnOffApp'),
			value: ScreenEnum.exit,
			displayNone: !state.apk || chromebox,
		}
	];

	useEffect(() => {
		dispatch({ type: 'SET_SCREEN', value: ScreenEnum.welcome });
		try {
			if (!localStorage.getItem('language'))
				localStorage.setItem('language', i18n.language || 'de');
			if (!localStorage.getItem('pairing'))
				localStorage.setItem('pairing', 'false');

			const useQs = localStorage.getItem('use_qs') === 'true';

			const qsUrl = localStorage.getItem('qs_url') || undefined;
			const pairingUrl = localStorage.getItem('url_pairing') || undefined;
			const deliveryUrl =
				localStorage.getItem('url_delivery') || undefined;
			const vitalsUrl = localStorage.getItem('url_vitals') || undefined;

			const brandName = localStorage.getItem('brand') || undefined;
			const marketName = localStorage.getItem('market') || undefined;

			if (brandName) dispatch({ type: 'SET_BRAND', value: brandName });

			if (marketName && state?.dataJson?.brands) {
				const brandObj = state.dataJson.brands.find(
					(b: any) => b.name === (brandName || state.brand)
				);
				const marketObj = brandObj?.markets?.find(
					(m: any) => m.name === marketName
				);
				if (marketObj) {
					dispatch({ type: 'SET_MARKET', value: marketObj });
					if (!useQs && marketObj.urls) {
						dispatch({
							type: 'SET_ENDPOINTS',
							value: marketObj.urls,
						});
					}
					if (useQs && marketObj.qsUrl && !qsUrl) {
						dispatch({ type: 'SET_URL', value: marketObj.qsUrl });
					}
				}
			}

			if (useQs) {
				if (qsUrl) dispatch({ type: 'SET_URL', value: qsUrl });
				// set endpoints to undefined for expert mode
				dispatch({ type: 'SET_ENDPOINTS', value: undefined as any });
			} else {
				if (pairingUrl || deliveryUrl || vitalsUrl) {
					dispatch({
						type: 'SET_ENDPOINTS',
						value: {
							pairing: pairingUrl,
							delivery: deliveryUrl,
							vitals: vitalsUrl,
						},
					});
				}
			}

			const pairing = localStorage.getItem('pairing') === 'true';
			const idToken = localStorage.getItem('id_token');
			const refreshToken = localStorage.getItem('refresh_token');

			if (pairing && idToken && refreshToken) {
				dispatch({
					type: 'SET_PINPAIRING_ACTIVATIONRESPONSE',
					value: {
						id_token: idToken,
						refresh_token: refreshToken,
						token_type: 'Bearer',
					},
				});
				dispatch({ type: 'SET_DEVICE', value: idToken });

				const lang = localStorage.getItem('language');
				if (lang && lang !== i18n.language) {
					i18n.changeLanguage(lang);
					dispatch({ type: 'SET_LANGUAGE', value: lang });
				}

				if (!localStorage.getItem('setup')) {
					dispatch({
						type: 'SET_SCREEN',
						value: ScreenEnum.pairDevice,
					});
					props.setScreen?.(ScreenEnum.pairDevice);
				}
			} else {
				if (state?.language)
					localStorage.setItem('language', state.language);
			}
		} catch (e) {
			console.debug('[welcome] resume init failed:', e);
		}
	}, []);

	const startFlow = () => {
		localStorage.setItem('pairing', 'false');
		dispatch({ type: 'SET_SCREEN', value: ScreenEnum.hardwareCheck });
		props.setScreen?.(ScreenEnum.hardwareCheck);
	};

	const onConfirmExistingSetup = () => {
		if (nextScreen === ScreenEnum.welcome) {
			setResetOverlay(true);
		} else {
			dispatch({ type: 'SET_SCREEN', value: nextScreen });
			props.setScreen?.(nextScreen);
		}
	};

	return (
		<>
			<div className={`top${state.setup ? ' less-padding' : ''}`}>
				<div className='welcome'>
					<h1>{t('WelcomeHeadline')}</h1>
					<p className='p1'>
						{t(
							state.setup
								? 'WelcomeExistingSetupText'
								: 'WelcomeText'
						)}
					</p>

					{props.version && (
						<>
							<h4>{t('WelcomeVersion')}</h4>
							<p className='p2'>{props.version}</p>
						</>
					)}

					{!state.setup ? (
						<label>
							<h4>{t('WelcomeLabelLanguage')}</h4>
							<Dropdown
								list={state.dataJson.languages}
								set={(value: string) => {
									i18n.changeLanguage(value);
									dispatch({ type: 'SET_LANGUAGE', value });
									localStorage.setItem('language', value);
								}}
								defaultValue={
									(
										state.dataJson.languages as Array<any>
									).find(
										lang => lang.value === state.language
									)?.label ||
									state.dataJson.languages[0].label
								}
							/>
						</label>
					) : (
						<RadioButtons
							options={options}
							onClick={(value: ScreenEnum) =>
								setNextScreen(value)
							}
						/>
					)}
				</div>
			</div>

			<div className='buttons'>
				{state.setup === null && (
					<button className='white' onClick={startFlow}>
						{t('ButtonStart')}
					</button>
				)}

				{state.setup !== null && (
					<>
						<button
							className='white'
							onClick={onConfirmExistingSetup}
						>
							{t('Confirm')}
						</button>
						<button
							className='cancel'
							onClick={() => {
								dispatch({
									type: 'SET_SCREEN',
									value: ScreenEnum.final,
								});
								props.setScreen?.(ScreenEnum.final);
							}}
						>
							{t('Cancel')}
						</button>
					</>
				)}
			</div>

			{resetOverlay && (
				<ResetOverlay
					setResetOverlay={setResetOverlay}
					setScreen={s => {
						dispatch({ type: 'SET_SCREEN', value: s });
						props.setScreen?.(s);
					}}
				/>
			)}
		</>
	);
}
